/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useEffect, useMemo, useState} from 'react';
import classNames from "classnames";
import {useStore} from "zustand";
import {Divider, Space, Table, TableColumnsType, Tag, Tooltip, Typography} from "antd";
import {CloseOutlined} from "@ant-design/icons";
import styles from './BaechaList2Table.module.scss';
import {BaechaList2TableItem, BaechaWorkDayItem} from "../BaechaList2.interface";
import {
	formatCurrency,
	getBaechaPriceReceiveMethodStr,
	getBaechaPriceTypeStr,
	getBaechaStatusStr,
	getStatusColor,
	getWorkPeriodStr
} from "../../../util/format";
import ExpandedBaechaRow from "../ExpandedBaechaRow/ExpandedBaechaRow";
import {getMMDDHHmmStr, getRegionPrefix, onOpenMapByCoordinates} from "../BaechaList2Util";
import {useBaechaList2Store} from "../../../stores/baechaList2Store";
import {useMessageStore} from "../../../stores/messageStore";
import Constants from "../../../constants";
import CustomPagination from "../../../components/common/paging/CustomPagination";

// [수정] renderCall 내부의 Typography에도 블록 스타일과 말줄임 조건을 명확히 주입
const renderCall = (item: BaechaList2TableItem, textStyle: React.CSSProperties) => {
	if (!item.sujuUserId) return null;

	const call = item.callPassItemList?.find((el) => el.callPassType === 'call' && el.userId === item.sujuUserId);
	if (!call || !call.distance) return null;

	const txt = `콜: (${call.distance.toFixed(1)}km)`;

	return (
		<Typography.Text
			key="call"
			className={classNames(styles.address, { [styles.cursorPointer]: (call.callRegionLongitude && call.callRegionLatitude) })}
			ellipsis={{ tooltip: txt }}
			style={textStyle} // 👈 상위에서 정의한 고정 스타일 주입
			onClick={(e) => {
				e.stopPropagation();
				onOpenMapByCoordinates({
					lon: call?.callRegionLongitude,
					lat: call?.callRegionLatitude,
				});
			}}
		>
			{txt}
		</Typography.Text>
	);
};

const onClickBaecha = (baechaNo: number) => {
	const url = `/app/business/baechaList2?baechaNo=${baechaNo}`;
	window.open(url, '_blank');
};

const calcWorkLengthByBaechaWorkDayList = (baechaWorkDayList: BaechaWorkDayItem[]) => {
	if (baechaWorkDayList.length === 0) return 0;
	if (baechaWorkDayList.length === 1) {
		const item = baechaWorkDayList[0];
		if (item.mhType === '1h') return 0.1;
		if (item.mhType === '2h') return 0.2;
		if (item.mhType === '4h') return 0.4;
		if (item.mhType === '1d') return 1;
	}
	return baechaWorkDayList.length;
}

const calcWorkLengthByBaecha = ({workDay, workHour}: {  workDay?: number, workHour?: number}) => {
	if (!workDay && !workHour) return 0;

	if (workDay && !workHour) {
		return workDay;
	}

	if (!workDay && workHour) {
		if (workHour === 1) return 0.1;
		if (workHour === 2) return 0.2;
		if (workHour === 4) return 0.4;
		if (workHour === 1) return 1.0;
	}

	if (workDay && workHour) {
		return workDay + 1;
	}

	return 0;
}

const getIsWorkExtended = (item: BaechaList2TableItem) => {
	const { workDay, workHour, baechaWorkDayList, isWorkMonth, status } = item;
	if (isWorkMonth) return false;
	if (status !== 'suju' && status !== 'complete') return false;

	const workLengthByBaechaWorkDay = calcWorkLengthByBaechaWorkDayList(baechaWorkDayList);
	const workLengthByBaecha = calcWorkLengthByBaecha({ workDay, workHour });

	return workLengthByBaechaWorkDay > workLengthByBaecha;
};

const BaechaList2Table = () => {
	const { setMessages } = useStore(useMessageStore);
	const data = useBaechaList2Store((s) => s.data);
	const page = useBaechaList2Store((s) => s.page);
	const pageSize = useBaechaList2Store((s) => s.pageSize);
	const setPage = useBaechaList2Store((s) => s.setPage);

	const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

	const onSearchPrevBaechaNumbers = (prevBaechaNoList: number[]) => {
		if (!prevBaechaNoList || prevBaechaNoList.length === 0) return;

		const baechaNoQuery = prevBaechaNoList.join(',');
		const url = `/app/business/baechaList2?baechaNo=${baechaNoQuery}`;

		window.open(url, '_blank');
	};

	const columns = useMemo<TableColumnsType<BaechaList2TableItem>>(() => [
		{
			title: (
				<>
					<p>번호</p>
					<p>상태</p>
				</>
			),
			key: 'baechaNo+status',
			align: 'center',
			width: 80,
			fixed: 'left',
			render: (_, item) => (
				<div
					style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' , justifyContent: 'center', cursor: 'pointer' }}
					onClick={(e) => {
						e.stopPropagation();
						navigator.clipboard.writeText(item.id)
							.then(() => setMessages('success', `배차 ID 복사: ${item.id}`))
							.catch(() => setMessages('error', '배차 ID 복사 실패'));
					}}
				>
					<div>{item.baechaNo}</div>
					<Tag color={getStatusColor(item.status)} style={{ margin: '0' }}>
						{getBaechaStatusStr(item.status)}
					</Tag>
				</div>
			),
		},
		{
			title: (
				<>
					<p>발주시간</p>
					<p>도착시간</p>
				</>
			),
			key: 'createDate+workArriveDate',
			align: 'center',
			width: 110,
			render: (_, item) => (
				<div style={{ textAlign: 'center'}}>
					<div style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
						{getMMDDHHmmStr(item.createDate)}
					</div>
					<div
						style={{
							fontVariantNumeric: 'tabular-nums',
							whiteSpace: 'nowrap',
							color: (item.baechaType === 'urgent') ? 'red' : '',
							fontWeight: (item.baechaType === 'urgent') ? 'bolder' : '',
						}}
					>
						{getMMDDHHmmStr(item.workArriveDate)}
					</div>
				</div>
			)
		},
		{
			title: '배차방',
			key: 'baljuGroupName',
			dataIndex: 'baljuGroupName',
			align: 'center',
			width: 80,
		},
		{
			title: '발주자',
			key: 'baljuUser',
			align: 'center',
			width: 120,
			render: (_, item) => {
				const infos = [
					item.baljuUserName,
					item.baljuUserNickname,
					item.baljuUserPhoneNum
				].filter(Boolean);

				const showCheck = !item.isHaveEvent && item.baljuGroupId === Constants.CALL_CENTER_GROUP_ID;

				return (
					<>
						{infos.map((info, index) => (
							<Typography.Text
								key={index}
								style={{
									width: '100%',
									display: 'block',
									borderBottom: index !== infos.length - 1 ? '1px solid #ECECEC' : 'none'
								}}
								ellipsis={{ tooltip: true }}
							>
								{showCheck && index === 0 && (
									<CloseOutlined style={{ color: 'red', marginRight: 4 }} />
								)}
								{info}
							</Typography.Text>
						))}
					</>
				);
			}
		},
		{
			title: '수주자',
			key: 'sujuUser',
			align: 'center',
			width: 120,
			render: (_, item) => {
				// 1. 배차 수주자의 콜패스 데이터 찾기
				const matchingCallPass = item.sujuUserId
					? item.callPassItemList?.find((el) => el.userId === item.sujuUserId)
					: null;

				const sujuPoint = matchingCallPass?.point;

				// 2. infos에는 순수 이름 문자열만 넣습니다. (포맷팅은 하단 렌더링 시 처리)
				const infos = [
					{ type: 'name', text: item.sujuUserName },
					{ type: 'nickname', text: item.sujuUserNickname },
					{ type: 'phone', text: item.sujuUserPhoneNum }
				].filter(info => info.text);

				const isOnlyCall = item.callPassItemList?.filter((el) => el.callPassType === 'call').length === 1;

				return (
					<>
						{infos.map((info, index) => {
							const isName = info.type === 'name';

							return (
								<Typography.Text
									key={index}
									style={{
										width: '100%',
										display: 'block',
										borderBottom: index !== infos.length - 1 ? '1px solid #ECECEC' : 'none',
									}}
									ellipsis={{ tooltip: true }}
								>
									{isName ? (
										<>
											{/* 이름 */}
											<span
												style={{
													color: isOnlyCall ? 'red' : 'inherit',
													fontWeight: isOnlyCall ? 'bolder' : 'inherit',
												}}
											>
                    {info.text}
                  </span>
											{/* 점수 */}
											{(sujuPoint || sujuPoint === 0) && (
												<span
													style={{
														color: sujuPoint <= -3 ? 'red' : 'inherit',
														fontWeight: sujuPoint <= -3 ? 'bolder' : 'inherit',
													}}
												>
                      ({sujuPoint})
                    </span>
											)}
										</>
									) : (
										info.text
									)}
								</Typography.Text>
							);
						})}
					</>
				);
			}
		},
		{
			title: '주소',
			key: 'address',
			align: 'center',
			width: 120,
			render: (_, item) => {
				const regionPrefix = getRegionPrefix(item.region);
				const workLoc = item.workLoc;
				const hasWorkLoc = !!(regionPrefix || workLoc);

				// [수정] 공통 스타일 객체를 하단 함수에 토스하기 위해 미리 정의
				const textStyle: React.CSSProperties = {
					display: 'block',
					width: '100%',
					textAlign: 'center',
					minWidth: 0,
				};

				const callContent = renderCall(item, textStyle);
				const hasCall = !!callContent;

				const sujuAddressText = item.sujuMemberSettingDistance
					? [
						`활동: (${item.sujuMemberSettingDistance.toFixed(1)}km)`,
						item.sujuMemberSettingLocation
					].filter(Boolean).join(' ')
					: null;
				const hasSuju = !!sujuAddressText;

				if (!hasWorkLoc && !hasCall && !hasSuju) {
					return <div style={{ height: '72px' }} />;
				}

				// [수정] 부모가 max-content 연산을 하더라도 120px 밖으로 탈출하지 못하도록 제어
				const cellWrapperStyle: React.CSSProperties = {
					width: '100%',
					maxWidth: '100%',
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
				};

				const lineRowStyle: React.CSSProperties = {
					height: '24px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					width: '100%',
					minWidth: 0,
					overflow: 'hidden',
				};

				const dividerStyle: React.CSSProperties = {
					borderBottom: '1px solid #ECECEC',
				};

				return (
					// [수정] Space 컴포넌트를 걷어내고 순수 CSS flex-column 구조로 변경하여 래퍼 버그 원천 차단
					<div style={cellWrapperStyle}>
						{/* 1번 줄: 작업지 주소 */}
						<div style={{ ...lineRowStyle, ...dividerStyle }}>
							{hasWorkLoc && (
								<Typography.Text
									ellipsis={{ tooltip: [regionPrefix, workLoc].filter(Boolean).join(' ') }}
									className={classNames(styles.address, {
										[styles.cursorPointer]: item.baechaLongitude && item.baechaLatitude,
									})}
									style={textStyle}
									onClick={(e) => {
										e.stopPropagation();
										onOpenMapByCoordinates({
											lat: item.baechaLatitude,
											lon: item.baechaLongitude,
										});
									}}
								>
									{regionPrefix && (
										<span style={{ background: '#EFF1F4', color: '#4A5E7A', padding: '0 5px', borderRadius: '5px', marginRight: '5px', display: 'inline-block', verticalAlign: 'middle' }}>
          {regionPrefix}
         </span>
									)}
									<span style={{ verticalAlign: 'middle' }}>{workLoc}</span>
								</Typography.Text>
							)}
						</div>

						{/* 2번 줄: 콜 */}
						<div style={{ ...lineRowStyle, ...dividerStyle }}>
							{callContent}
						</div>

						{/* 3번 줄: 활동: 멤버 세팅 */}
						<div style={lineRowStyle}>
							{hasSuju && (
								<Typography.Text
									className={classNames(styles.address, { [styles.cursorPointer]: (item.sujuMemberSettingLongitude && item.sujuMemberSettingLatitude) })}
									ellipsis={{ tooltip: sujuAddressText }}
									style={textStyle}
									onClick={(e) => {
										e.stopPropagation();
										onOpenMapByCoordinates({
											lat: item.sujuMemberSettingLatitude,
											lon: item.sujuMemberSettingLongitude,
										});
									}}
								>
									{sujuAddressText}
								</Typography.Text>
							)}
						</div>
					</div>
				);
			}
		},
		{
			title: (
				<>
					<p>톤수</p>
					<p>기간</p>
				</>
			),
			key: 'carType+workDay+workHour',
			align: 'center',
			width: 60,
			render: (_, item) => (
				<>
					<p>
						{item.carType}
					</p>
					<p>
						{getWorkPeriodStr({ workDay: item.workDay, workHour: item.workHour})}
					</p>
				</>
			)
		},
		{
			title: (
				<>
					<p>총액</p>
					<p>수수료</p>
				</>
			),
			key: 'price+fee',
			align: 'center',
			width: 100,
			render: (_, item) => (
				<>
					<p style={{ textAlign: 'right' }}>
						{formatCurrency(item.price, 'KRW')}
					</p>
					<p style={{ textAlign: 'right' }}>
						{formatCurrency(item.fee, 'KRW')}
					</p>
				</>
			)
		},
		{
			title: (
				<>
					<p>단가</p>
					<p>결제</p>
				</>
			),
			key: 'priceType+priceReceiveMethod',
			align: 'center',
			width: 60,
			render: (_, item) => (
				<>
					<p>{getBaechaPriceTypeStr(item.priceType)}</p>
					<p>{getBaechaPriceReceiveMethodStr(item.priceReceiveMethod)}</p>
				</>
			)
		},
		{
			title: '점수',
			key: 'baljuPoint+sujuPoint',
			align: 'center',
			width: 60,
			render: (_, item) => (
				<>
					{item.baljuPoint ? <p>{`+${item.baljuPoint}`}</p> : null}
					{item.sujuPoint ? <p>{`-${item.sujuPoint}`}</p> : null}
				</>
			)
		},
		{
			title: '작업내용',
			key: 'workContent',
			align: 'center',
			width: 200,
			render: (_, item) => (
				<Typography.Paragraph
					ellipsis={{ rows: 3, tooltip: true }}
					style={{ margin: 0, width: '100%', textAlign: 'left' }}
				>
					{item.workContent}
				</Typography.Paragraph>
			)
		},
		{
			title: '특이사항',
			key: 'workEtc',
			align: 'center',
			width: 200,
			render: (_, item) => (
				<Typography.Paragraph
					ellipsis={{ rows: 3, tooltip: true }}
					style={{ margin: 0, width: '100%', textAlign: 'left' }}
				>
					{item.workEtc}
				</Typography.Paragraph>
			)
		},
		{
			title: '어뷰징 관련',
			key: 'abusing',
			align: 'center',
			width: 120,
			render: (_, item) => {
				const carList = item.sujuMemberCarList ?? [];
				const isWorkExtended = getIsWorkExtended(item);
				const hasBaecha = item.previousBaechaNoList && item.previousBaechaNoList.length > 0;
				const hasCar = carList.length > 0;


				const prevBaechaNoList = item.previousBaechaNoList?.map((el) => el.baechaNo) ?? [];
				const sortedBaechaList = [...prevBaechaNoList, item.baechaNo].sort((a, b) => b - a);


				if (!hasBaecha && !hasCar && !isWorkExtended) {
					return null;
				}

				return (
					<div style={{ width: 100, overflow: 'hidden', margin: '0 auto' }}>
						<Space
							direction="vertical"
							size={0}
							style={{ width: '100%', display: 'flex' }}
							split={<Divider style={{ margin: 0, borderColor: '#ECECEC' }} />}
						>
							{/* 배차 번호 영역 */}
							<div style={{ width: '100%', textAlign: 'left' }}>
								{hasBaecha ? (
									<Tooltip
										placement="left"
										title={
											<div style={{ maxHeight: 200, overflowY: 'auto' }}>
												{sortedBaechaList.map((baechaNo, idx) => (
													<span
														key={`tooltip_${idx}`}
														onClick={(e) => {
															e.stopPropagation();
															onClickBaecha(baechaNo);
														}}
														className={styles.cursorPointer}
														style={{ display: 'inline-block', marginRight: 6 }}
													>
                            {baechaNo}
														{idx !== sortedBaechaList.length - 1 && ','}
                          </span>
												))}

												<span
													onClick={(e) => {
														e.stopPropagation();
														onSearchPrevBaechaNumbers(sortedBaechaList);
													}}
													className={styles.cursorPointer}
													style={{ display: 'inline-block', marginRight: 6 }}
												>
													조회
												</span>
											</div>
										}
									>
										<Typography.Text
											style={{ width: '100%', display: 'block', textAlign: 'left', whiteSpace: 'nowrap' }}
											ellipsis
										>
											{sortedBaechaList.map((baechaNo, idx) => (
												<span
													key={`text_${idx}`}
													onClick={(e) => {
														e.stopPropagation();
														onClickBaecha(baechaNo);
													}}
													className={styles.cursorPointer}
												>
                          {baechaNo}
													{idx !== sortedBaechaList.length - 1 && ', '}
                        </span>
											))}
										</Typography.Text>
									</Tooltip>
								) : (
									<span style={{ display: 'block' }}>&nbsp;</span>
								)}
							</div>

							{/* 차량 영역 */}
							<div style={{ width: '100%', textAlign: 'left' }}>
								{hasCar ? (
									<Typography.Text
										style={{ width: '100%', display: 'block', textAlign: 'left', whiteSpace: 'nowrap' }}
										ellipsis={{ tooltip: { placement: 'left' } }}
									>
										{carList.map((el, idx) => (
											<span
												key={`${item.id}_${idx}`}
												style={el.isActive ? { } : { color: 'grey', fontWeight: 'lighter' }}
											>
                {el.carType}
												{idx !== carList.length - 1 && ', '}
              </span>
										))}
									</Typography.Text>
								) : (
									<span style={{ display: 'block' }}>&nbsp;</span>
								)}
							</div>

							{/* 작업 일정 영역 */}
							<div style={{ width: '100%', textAlign: 'left' }}>
								{isWorkExtended ? (
									<div style={{ color: 'red', fontWeight: 'bolder', whiteSpace: 'nowrap' }}>
										일정 증가
									</div>
								) : (
									<div style={{ whiteSpace: 'nowrap' }}>&nbsp;</div>
								)}
							</div>
						</Space>
					</div>
				);
			},
		},
	], []);

	useEffect(() => {
		if (data?.length === 1) {
			setExpandedRowKeys([data[0].id]);
		} else {
			setExpandedRowKeys([]);
		}
	}, [data]);

	return (
		<div className={styles.wrap}>
			<Table
				rowKey="id"
				columns={columns}
				dataSource={data ?? []}
				tableLayout="fixed"
				scroll={{ x: 1200 }}
				bordered
				rowHoverable={false}
				rowClassName={(record, index) => (index % 2 === 0 ? styles.evenRow : styles.oddRow )}
				pagination={false}
				expandable={{
					expandedRowRender: (record: BaechaList2TableItem) => (
						<ExpandedBaechaRow data={record} />
					),
					expandedRowKeys,
					onExpandedRowsChange: (keys) => {
						setExpandedRowKeys(keys as React.Key[]);
					},
				}}
			/>

			<div style={{ marginTop: '32px' }}>
				<CustomPagination
					currentPage={page}
					pageSize={pageSize}
					currentDataLength={data?.length ?? 0}
					onPageChange={(targetPage) => setPage(targetPage)}
				/>
			</div>
		</div>
	);
};

export default BaechaList2Table;